import { GameResult, PortfolioGameResult } from '../types';
import {
  isSameContainerEntry,
  rankContainerEntry,
} from './containerLeaderboard.ts';
import {
  isSameSurvivorEntry,
  rankSurvivorEntry,
} from './survivorLeaderboard.ts';

const TABLE_SIZE = 10;

export type LeaderboardDisplayRow<T> = {
  key: string;
  rank: number | string;
  result: T;
  highlight: boolean;
};

function entryKey(result: { id?: string; timestamp: number }, index: number): string {
  return result.id ?? `run-${result.timestamp}-${index}`;
}

export function buildContainerLeaderboardRows(
  sorted: GameResult[],
  playerResult?: GameResult | null
): LeaderboardDisplayRow<GameResult>[] {
  if (!playerResult) {
    return sorted.slice(0, TABLE_SIZE).map((result, index) => ({
      key: entryKey(result, index),
      rank: index + 1,
      result,
      highlight: false,
    }));
  }

  const playerRank = rankContainerEntry(sorted, playerResult);
  const inTopTen =
    playerRank <= TABLE_SIZE &&
    sorted.slice(0, TABLE_SIZE).some((entry) => isSameContainerEntry(entry, playerResult));

  if (inTopTen) {
    return sorted.slice(0, TABLE_SIZE).map((result, index) => ({
      key: entryKey(result, index),
      rank: index + 1,
      result,
      highlight: isSameContainerEntry(result, playerResult),
    }));
  }

  const rows = sorted.slice(0, TABLE_SIZE - 1).map((result, index) => ({
    key: entryKey(result, index),
    rank: index + 1,
    result,
    highlight: false,
  }));

  rows.push({
    key: entryKey(playerResult, rows.length),
    rank: playerRank,
    result: playerResult,
    highlight: true,
  });

  return rows;
}

export function buildSurvivorLeaderboardRows(
  sorted: PortfolioGameResult[],
  playerResult?: PortfolioGameResult | null
): LeaderboardDisplayRow<PortfolioGameResult>[] {
  if (!playerResult) {
    return sorted.slice(0, TABLE_SIZE).map((result, index) => ({
      key: entryKey(result, index),
      rank: index + 1,
      result,
      highlight: false,
    }));
  }

  const playerRank = playerResult.won ? rankSurvivorEntry(sorted, playerResult) : null;
  const inTopTen =
    playerRank != null &&
    playerRank <= TABLE_SIZE &&
    sorted.slice(0, TABLE_SIZE).some((entry) => isSameSurvivorEntry(entry, playerResult));

  if (inTopTen) {
    return sorted.slice(0, TABLE_SIZE).map((result, index) => ({
      key: entryKey(result, index),
      rank: index + 1,
      result,
      highlight: isSameSurvivorEntry(result, playerResult),
    }));
  }

  const rows = sorted.slice(0, TABLE_SIZE - 1).map((result, index) => ({
    key: entryKey(result, index),
    rank: index + 1,
    result,
    highlight: false,
  }));

  rows.push({
    key: entryKey(playerResult, rows.length),
    rank: playerRank ?? '—',
    result: playerResult,
    highlight: true,
  });

  return rows;
}
